import _get from 'lodash/get';
import {
	RUNNIT_NODE_FIELD_TYPE,
	RunnitNodeField,
} from '@run-diffusion/shared';

export const ASPECT_RATIO_MAP = [
	// Wide
	{ label: '3:1', value: 3.0 },
	{ label: '2.5:1', value: 2.5 },
	{ label: '21:9', value: 2.3 },
	{ label: '16:9', value: 1.8 },
	{ label: '3:2', value: 1.5 },
	{ label: '4:3', value: 1.33 },
	{ label: '5:4', value: 1.25 },
	{ label: '1:1', value: 1.0 },
	// Portrait
	{ label: '3:4', value: 0.75 },
	{ label: '2:3', value: 0.67 },
	{ label: '9:16', value: 0.6 },
	{ label: '9:21', value: 0.4 },
	{ label: '1:3', value: 0.33 },
];

export const getAspectRatioOverrideSourceFieldDefUuid = (
	field: RunnitNodeField,
	inputValues: Record<string, any>,
): string | null => {
	if (!field || field.type !== RUNNIT_NODE_FIELD_TYPE.WIDTH_HEIGHT) return null;

	const logicField = _get(field, 'logic.field') || {};
	for (const fieldDefUuid of Object.keys(logicField)) {
		const disable = logicField[fieldDefUuid] && logicField[fieldDefUuid].disable;
		if (!disable || !disable.imageAspectRatioOverride) continue;

		const inputValue = (inputValues || {})[fieldDefUuid];
		const disableTriggered = !!(
			(disable.onValue === true && inputValue) ||
			(Array.isArray(disable.onValue) && disable.onValue.some((value: any) => value === inputValue))
		);
		if (!disableTriggered || !hasAspectRatioDimensions(inputValue)) continue;

		return fieldDefUuid;
	}

	return null;
};

export const getAspectRatioOverrideDisableConfig = (
	field: RunnitNodeField,
	inputValues: Record<string, any>,
): any => {
	const sourceFieldDefUuid = getAspectRatioOverrideSourceFieldDefUuid(field, inputValues);
	if (!sourceFieldDefUuid) return null;

	const logicField = _get(field, 'logic.field') || {};
	return (logicField[sourceFieldDefUuid] && logicField[sourceFieldDefUuid].disable) || null;
};

export const getMatchingInputAspectRatio = (inputValue: any): { label: string, value: number } | null => {
	const dimensions = getInputDimensions(inputValue);
	if (!dimensions) return null;

	const aspectRatio = Math.round((dimensions.width / dimensions.height) * 10) / 10;
	let closestMatch = ASPECT_RATIO_MAP[0];
	let smallestDiff = Infinity;

	for (const match of ASPECT_RATIO_MAP) {
		const diff = Math.abs(match.value - aspectRatio);
		if (diff < smallestDiff) {
			smallestDiff = diff;
			closestMatch = match;
		}
	}

	return closestMatch;
};

export const getAspectRatioDisabledMessage = (
	field: RunnitNodeField,
	inputValues: Record<string, any>,
): string | null => {
	const sourceFieldDefUuid = getAspectRatioOverrideSourceFieldDefUuid(field, inputValues);
	if (!sourceFieldDefUuid) return null;

	const matchingAspectRatio = getMatchingInputAspectRatio((inputValues || {})[sourceFieldDefUuid]);
	if (!matchingAspectRatio) return null;

	return `Aspect Ratio will match input image (${matchingAspectRatio.label})`;
};

export const applyAspectRatioDisabledMessage = (
	logicOnFields: any,
	field: RunnitNodeField,
	inputValues: Record<string, any>,
): any => {
	const message = getAspectRatioDisabledMessage(field, inputValues);
	const disableConfig = getAspectRatioOverrideDisableConfig(field, inputValues);
	if (!message || !disableConfig) return logicOnFields;

	const idx = logicOnFields.triggeredDisables.findIndex((disable: any) => disable === disableConfig);
	if (idx === -1) return logicOnFields;

	logicOnFields.triggeredDisables.splice(idx, 1, {
		...logicOnFields.triggeredDisables[idx],
		message,
	});

	return logicOnFields;
};

const hasAspectRatioDimensions = (inputValue: any): boolean => {
	return !!getInputDimensions(inputValue);
};

const getInputDimensions = (inputValue: any): { width: number, height: number } | null => {
	const width =
		_get(inputValue, 'image.nodeRunResult.file.width') ||
		_get(inputValue, 'nodeRunResult.file.width') ||
		_get(inputValue, 'image.upload.width') ||
		_get(inputValue, 'upload.width') ||
		_get(inputValue, 'video.nodeRunResult.file.width') ||
		_get(inputValue, 'video.upload.width');
	const height =
		_get(inputValue, 'image.nodeRunResult.file.height') ||
		_get(inputValue, 'nodeRunResult.file.height') ||
		_get(inputValue, 'image.upload.height') ||
		_get(inputValue, 'upload.height') ||
		_get(inputValue, 'video.nodeRunResult.file.height') ||
		_get(inputValue, 'video.upload.height');

	if (!width || !height) return null;

	return { width, height };
};
